import * as yup from 'yup';
import type {
  AcknowledgmentSettings,
  Appointments,
  Businesses,
  CustomerGuests,
  Customers,
  Employees,
  IdentificationType,
  MedicalHistorySettings,
  Services,
  StaticWaiverSettings,
  Users,
  WaiverEmergencyContactSettings,
  WaiverEmployeeSettings,
  WaiverEvents,
  WaiverIdentificationSettings,
  WaiverIdentificationSettingsTypes,
  WaiverMinorSettings,
  WaiverOptionType,
  WaiverOptions,
  WaiverRequests,
  Waivers,
} from '@prisma/client';
import { SORTABLE_KEYS } from '../../constants/waivers';

export type WaiverSettingsType =
  | 'static'
  | 'acknowledgments'
  | 'medical'
  | 'identification'
  | 'employee'
  | 'address'
  | 'emergencyContact'
  | 'minor';

export interface StaticLiabilityWaiverValues {
  document: string | File | null;
  attestationText: string;
}

export interface AcknowledgmentValues {
  intro: string;
  options: WaiverOptions[];
}

export interface MedicalHistoryValues {
  intro: string;
  options: WaiverOptions[];
}

export interface EmployeeValues {
  options: WaiverOptions[];
}

export interface AcknowledgmentSettingsData extends AcknowledgmentSettings {
  options: WaiverOptions[];
}

export type EmergencyContactEnabledField =
  | 'name'
  | 'phone'
  | 'email'
  | 'address';

export interface EmergencyContactValues {
  enabledFields: EmergencyContactEnabledField[];
}

export interface MinorFormValues {
  requireGuardianInformation: boolean;
  requireGuardianSignature: boolean;
  requireRecipientSignature: boolean;
  acknowledgmentOptions: WaiverOptions[];
}

export interface MedicalHistorySettingsData extends MedicalHistorySettings {
  options: WaiverOptions[];
}

export interface WaiverIdentificationSettingsData
  extends WaiverIdentificationSettings {
  types: WaiverIdentificationSettingsTypes[];
}

export interface WaiverEmployeeSettingsData extends WaiverEmployeeSettings {
  options: WaiverOptions[];
}

export interface WaiverMinorSettingsData extends WaiverMinorSettings {
  acknowledgmentOptions: WaiverOptions[];
}

export interface GetWaiversData extends Waivers {
  acknowledgmentSettings: Omit<AcknowledgmentSettingsData, 'id'>;
  medicalHistorySettings: Omit<MedicalHistorySettingsData, 'id'>;
  staticWaiverSettings: Omit<StaticWaiverSettings, 'id'>;
  identificationSettings: Omit<WaiverIdentificationSettingsData, 'id'>;
  employeeSettings: Omit<WaiverEmployeeSettingsData, 'id'>;
  emergencyContactSettings: Omit<WaiverEmergencyContactSettings, 'id'>;
  minorSettings: Omit<WaiverMinorSettingsData, 'id'> | null;
  services: Pick<Services, 'serviceId' | 'serviceName'>[];
}
export interface WaiverRequestWithCustomerAndEvents extends WaiverRequests {
  waiver: Pick<Waivers, 'name'>;
  customer: Pick<Customers, 'dob'> & {
    user: Pick<Users, 'firstName' | 'lastName' | 'phoneNumber' | 'email'>;
  };
  events: WaiverEvents[];
  appointment: Pick<
    Appointments,
    | 'startDateTime'
    | 'onschedId'
    | 'appointmentId'
    | 'status'
    | 'checkoutSessionCompleted'
    | 'customerGuestId'
    | 'isForSomeoneElse'
    | 'recipientFirstName'
    | 'recipientLastName'
    | 'recipientDob'
    | 'recipientUserRelation'
  > & {
    customerGuest?: Pick<
      CustomerGuests,
      'id' | 'firstName' | 'lastName' | 'dob' | 'email' | 'relation'
    > | null;
    employees: Pick<Employees, 'displayName'>;
  };
}

export interface GetWaiverRequestsResponse {
  waiverRequests: WaiverRequestWithCustomerAndEvents[];
  totalCount: number;
}

export interface GetWaiverRequestResponse {
  waiverRequest: WaiverRequests & { isIDError: boolean };
  waiver: GetWaiversData & {
    business: Pick<Businesses, 'businessId'>;
  };
}

export interface PatchWaiverRequestPayload {
  businessId: string;
  waiverRequestId: string;
}

export interface PublishWaiversResponse extends Waivers {}

export const baseSettingsSchema = yup.object({
  enabled: yup.boolean().optional(),
});

export const staticWaiverSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    documentLink: yup.string().nullable().optional(),
    attestationText: yup
      .string()
      .min(1, "Attestation text can't be empty")
      .optional(),
  }),
);

const ids = ['ID', 'BIRTH_CERTIFICATE', 'GUARDIAN_ID'] as const;
export const identificationSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    allowPassportsAsId: yup.boolean().optional(),
    types: yup
      .array(
        yup.object({
          type: yup
            .mixed<IdentificationType>()
            .oneOf(ids, 'Please select a valid ID type')
            .required(),
          enabled: yup.boolean().required(),
          text: yup.string().optional(),
        }),
      )
      .length(ids.length, 'Invalid types selected')
      .optional(),
  }),
);

export const waiverOption = yup.object({
  id: yup.string().required(),
  type: yup
    .mixed<WaiverOptionType>()
    .oneOf([
      'CHECKBOX',
      'TEXT',
      'MULTILINE_TEXT',
      'SIGNATURE',
      'SINGLE_IMAGE',
      'MULTIPLE_IMAGE',
    ])
    .required(),
  text: yup
    .string()
    .test('required', 'Text is required', value => !!value?.trim())
    .required(),
  order: yup.number().required(),
});

export const acknowledgmentSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    intro: yup.string().optional(),
    options: yup.array(waiverOption).optional(),
  }),
);

export const medicalHistorySettingsSchema = baseSettingsSchema.concat(
  yup.object({
    intro: yup.string().optional(),
    options: yup.array(waiverOption).optional(),
  }),
);

export const employeeSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    options: yup.array(waiverOption).optional(),
  }),
);

export const emergencyContactSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    requireName: yup.boolean().optional(),
    requirePhone: yup.boolean().optional(),
    requireEmail: yup.boolean().optional(),
    requireAddress: yup.boolean().optional(),
  }),
);

export const waiverNameSchema = yup
  .string()
  .transform(value => (typeof value === 'string' ? value.trim() : value))
  .min(1, 'Name is required');

export const getUpdateWaiverNameSchema = (currentNames: string[]) =>
  yup.object({
    name: waiverNameSchema
      .notOneOf(currentNames, 'Waiver with this name already exists')
      .required(),
  });

export const minorSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    requireGuardianInformation: yup.boolean().optional(),
    requireGuardianSignature: yup.boolean().optional(),
    requireRecipientSignature: yup.boolean().optional(),
    acknowledgmentOptions: yup.array(waiverOption).optional(),
  }),
);

export const updateWaiverSchema = yup.object({
  businessId: yup.string().required(),
  staticWaiverSettings: staticWaiverSettingsSchema.optional(),
  identificationSettings: identificationSettingsSchema.optional(),
  acknowledgmentSettings: acknowledgmentSettingsSchema.optional(),
  medicalHistorySettings: medicalHistorySettingsSchema.optional(),
  employeeSettings: employeeSettingsSchema.optional(),
  collectAddress: yup.boolean().optional(),
  emergencyContactSettings: emergencyContactSettingsSchema.optional(),
  minorSettings: minorSettingsSchema.optional(),
  update: yup.boolean().optional(),
  name: waiverNameSchema.optional(),
});

export const createWaiverSchema = yup.object({
  businessId: yup.string().required(),
  name: waiverNameSchema.required(),
  serviceIds: yup
    .array(yup.number().integer().required())
    .nullable()
    .optional(),
});

export const getEmployeeWaiverQuestionsSchema = (options: WaiverOptions[]) =>
  yup.object(
    options.reduce(
      (schema, option) => {
        // eslint-disable-next-line no-param-reassign
        schema[option.id] =
          option.type === 'TEXT' || option.type === 'MULTILINE_TEXT'
            ? yup.string().required('Please write an answer')
            : option.type === 'SIGNATURE'
            ? yup.string().min(1, 'Signature is required').required()
            : option.type === 'SINGLE_IMAGE'
            ? yup
                .string()
                .min(1, 'Image is required')
                .required('Image is required')
            : option.type === 'MULTIPLE_IMAGE'
            ? yup
                .array(yup.string().required())
                .min(1, 'At least one image is required')
                .required('At least one image is required')
            : yup.boolean().required();
        return schema;
      },
      {} as Record<string, yup.AnySchema>,
    ),
  );

export type UpdateWaiverPayload = yup.InferType<typeof updateWaiverSchema>;

export type CreateWaiverPayload = yup.InferType<typeof createWaiverSchema>;

export type WaiverOption = yup.InferType<typeof waiverOption>;

export interface UpdateWaiverResponse extends PublishWaiversResponse {}

export interface CreateWaiverResponse extends GetWaiversData {}

export type SortableKeys = (typeof SORTABLE_KEYS)[number];

export type EmployeeWaiverQuestions = yup.InferType<
  ReturnType<typeof getEmployeeWaiverQuestionsSchema>
>;

export type SubmitEmployeeWaiverPayload = {
  waiverRequestId: string;
  answers: EmployeeWaiverQuestions;
  signedFileLink: string;
};

export type WaiverResponseData = {
  customerDetails: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    dob: string;
    address?: {
      streetAddress: string;
      streetAddress2: string | undefined;
      city: string;
      state: string;
      zip: string;
    };
  };
  acknowledgments: Record<string, boolean>;
  medicalHistory: Record<string, boolean | string>;
  employee?: Record<string, boolean | string>;
};

export interface AddIDToWaiverRequestPayload {
  waiverRequestId: string;
  idType: IdentificationType;
  idUrl: string;
  idMediaType: string;
  pdfUrl: string;
}

export interface AddIDToWaiverRequestResponse {
  signedFileLink: string;
  identificationImageLink: string;
}

export type WaiverSubmissionStatus =
  | 'QUEUED'
  | 'CANCELED'
  | 'SENT'
  | 'SIGNED'
  | 'PENDING_EMPLOYEE';
